// @ts-check
import React, { useCallback, useLayoutEffect, useRef, useState } from "react"
import { isToolUIPart } from "ai"
import { clsx } from "../../../../../utils/strings.utils"
import AssistantActivityPanel from "./AssistantActivityPanel"
import AssistantMarkdown from "./AssistantMarkdown"
import AssistantStreamingIndicator from "./AssistantStreamingIndicator"
import AskQuestionsCard from "./AskQuestionsCard"
import MessageBubble from "./MessageBubble"
import ToolApprovalCard from "./ToolApprovalCard"
import UserMessageFileParts from "./UserMessageFileParts"
import {
  assistantPartsToRenderItems,
  toolIdFromPart,
  userMessageTextFromParts,
} from "./uiMessageParts"

/**
 * @typedef {import("ai").UIMessage} UIMessage
 */

/**
 * @typedef {object} MessageListProps
 * @property {UIMessage[]} messages
 * @property {boolean} [streaming]
 * @property {import("react").ReactNode} [errorBanner]
 * @property {string} [className]
 * @property {import("ai").ChatAddToolApproveResponseFunction} [addToolApprovalResponse]
 * @property {import("ai").ChatAddToolOutputFunction<import("ai").UIMessage>} [addToolOutput]
 */

/**
 * @param {{
 *   message: UIMessage;
 *   streaming: boolean;
 *   addToolApprovalResponse?: import("ai").ChatAddToolApproveResponseFunction;
 *   addToolOutput?: import("ai").ChatAddToolOutputFunction<import("ai").UIMessage>;
 * }} props
 */
function AssistantMessageBody({
  message: m,
  streaming,
  addToolApprovalResponse,
  addToolOutput,
}) {
  const parts = m.parts ?? []
  const items = assistantPartsToRenderItems(parts)
  const [busyApprovalId, setBusyApprovalId] = useState(
    /** @type {string | null} */ (null)
  )
  const [busyAskToolCallId, setBusyAskToolCallId] = useState(
    /** @type {string | null} */ (null)
  )

  const approvalParts = parts.filter(
    (p) =>
      isToolUIPart(p) &&
      p.state === "approval-requested" &&
      "approval" in p &&
      typeof p.approval === "object" &&
      p.approval != null &&
      "id" in p.approval
  )

  const askPendingParts = parts.filter(
    (p) =>
      isToolUIPart(p) &&
      toolIdFromPart(p) === "ask_questions" &&
      p.state === "input-available"
  )

  const handleAskQuestionsSubmit = useCallback(
    /**
     * @param {string} toolCallId
     * @param {string} output
     */
    async (toolCallId, output) => {
      if (!addToolOutput) return
      setBusyAskToolCallId(toolCallId)
      try {
        await Promise.resolve(
          addToolOutput({
            tool: "ask_questions",
            toolCallId,
            output,
          })
        )
      } catch (e) {
        console.error("[estimation-agent] addToolOutput ask_questions", e)
      } finally {
        setBusyAskToolCallId(null)
      }
    },
    [addToolOutput]
  )

  const handleApprovalDecision = useCallback(
    /**
     * @param {string} approvalId
     * @param {boolean} approved
     */
    async (approvalId, approved) => {
      if (!addToolApprovalResponse) return
      setBusyApprovalId(approvalId)
      try {
        await Promise.resolve(
          addToolApprovalResponse({ id: approvalId, approved })
        )
      } catch (e) {
        console.error("[estimation-agent] addToolApprovalResponse", e)
      } finally {
        setBusyApprovalId(null)
      }
    },
    [addToolApprovalResponse]
  )

  const hasBody =
    items.length > 0 ||
    approvalParts.length > 0 ||
    askPendingParts.length > 0

  if (!hasBody) {
    return streaming ?
        <AssistantStreamingIndicator />
      : null
  }

  let lastActivitiesItemIndex = -1
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].type === "activities") {
      lastActivitiesItemIndex = i
      break
    }
  }

  return (
    <>
      {items.map((item, index) =>
        item.type === "activities" ?
          <AssistantActivityPanel
            key={item.id}
            segments={item.segments}
            streaming={streaming && index === lastActivitiesItemIndex}
          />
        : item.type === "ask-user-bubble" ?
          <MessageBubble key={item.id}>{item.text}</MessageBubble>
        : <AssistantMarkdown
            key={item.id}
            content={item.text}
            className="estimation-agent-quick-v3__assistant-md"
          />
      )}
      {addToolOutput ?
        askPendingParts.map((part) => {
          const dp = /** @type {import("ai").DynamicToolUIPart} */ (part)
          const tc =
            "toolCallId" in dp && typeof dp.toolCallId === "string" ?
              dp.toolCallId
            : dp.type
          return (
            <div
              key={tc}
              className="estimation-agent-quick-v3__tool-approval-shell"
            >
              <AskQuestionsCard
                part={dp}
                busy={busyAskToolCallId === tc}
                onSubmit={(output) => handleAskQuestionsSubmit(tc, output)}
              />
            </div>
          )
        })
      : null}
      {addToolApprovalResponse ?
        approvalParts.map((part) => {
          const toolPart =
            /** @type {import("ai").ToolUIPart | import("ai").DynamicToolUIPart} */ (
              part
            )
          return (
            <div
              key={
                "toolCallId" in toolPart &&
                typeof toolPart.toolCallId === "string" ?
                  toolPart.toolCallId
                : toolPart.type
              }
              className="estimation-agent-quick-v3__tool-approval-shell"
            >
              <ToolApprovalCard
                part={toolPart}
                busy={
                  toolPart.state === "approval-requested" &&
                  busyApprovalId === toolPart.approval.id
                }
                onDecision={handleApprovalDecision}
              />
            </div>
          )
        })
      : null}
      {streaming ?
        <AssistantStreamingIndicator />
      : null}
    </>
  )
}

/**
 * @param {MessageListProps} props
 */
export default function MessageList({
  messages,
  streaming = false,
  errorBanner,
  className,
  addToolApprovalResponse,
  addToolOutput,
}) {
  const bottomRef = useRef(/** @type {HTMLDivElement | null} */ (null))

  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior: "auto" })
  }, [messages, streaming, errorBanner])

  const lastIndex = messages.length - 1
  const lastMessage = lastIndex >= 0 ? messages[lastIndex] : null
  const showAwaitingAssistantRow =
    Boolean(streaming && lastMessage && lastMessage.role === "user")

  return (
    <div
      className={clsx("estimation-agent-quick-v3__messages", className)}
    >
      {errorBanner ?
        <div
          role="alert"
          className="estimation-agent-quick-v3__chat-error"
        >
          {errorBanner}
        </div>
      : null}
      {messages.map((m, i) => {
        const isLastAssistant =
          m.role === "assistant" && i === lastIndex && lastIndex >= 0
        const showStreamOnThis =
          Boolean(streaming && isLastAssistant)
        const userText =
          m.role === "user" ? userMessageTextFromParts(m) : ""

        return m.role === "assistant" ?
            <div
              key={m.id}
              className="estimation-agent-quick-v3__assistant-message"
            >
              <AssistantMessageBody
                message={m}
                streaming={showStreamOnThis}
                addToolApprovalResponse={addToolApprovalResponse}
                addToolOutput={addToolOutput}
              />
            </div>
          : <div
              key={m.id}
              className="estimation-agent-quick-v3__user-message-group"
            >
              <UserMessageFileParts parts={m.parts} />
              {userText.trim() ?
                <MessageBubble collapsibleMaxPx={200}>
                  {userText}
                </MessageBubble>
              : null}
            </div>
      })}
      {showAwaitingAssistantRow ?
        <div
          className="estimation-agent-quick-v3__assistant-message"
          aria-busy="true"
          aria-live="polite"
        >
          <AssistantStreamingIndicator />
        </div>
      : null}
      <div
        ref={bottomRef}
        className="estimation-agent-quick-v3__messages-end"
        aria-hidden
      />
    </div>
  )
}
